import Image from 'next/image';
import { useIntl } from 'react-intl';

import { Modal } from '../../../../../../design-system/components/modal/Modal';
import { useModal } from '../../../../../../design-system/components/modal/useModal';
import { RATIO, Ratio } from '../../../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../../../design-system/components/typography/Typography';
import { imageLoaderForPicture } from '../../../../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { UgcHighlight } from '../../../../../../resources/ugcHighlight/domain/types/ugcHighlight';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';

import style from './TravelerUgcModal.module.css';

type TravelerUgcModalProps = {
    isOpen: boolean;
    onClose: () => void;
    travelerUgc: PickDomain<
        UgcHighlight,
        'id' | 'segment' | 'username' | 'description' | 'pictures',
        {
            destination: PickDomain<Destination, 'id' | 'name' | 'slug' | 'preposition'>;
        }
    >;
};

export function TravelerUgcModal({ isOpen, onClose, travelerUgc }: TravelerUgcModalProps) {
    const { formatMessage } = useIntl();
    const modalProps = useModal();

    const imageRatio = RATIO['3/4'];

    return (
        <Modal {...modalProps} isOpen={isOpen} onClose={onClose}>
            <div className={style.container}>
                <div className={style.header}>
                    <div className={style.title}>{travelerUgc.username}</div>
                    <Typography as="div" scale="primary-body-regular">
                        {formatMessage(
                            {
                                id: `page.home.ugcSection.modal.${travelerUgc.segment}.subtitle`,
                            },
                            {
                                destinationPreposition: travelerUgc.destination.preposition,
                                destinationName: travelerUgc.destination.name,
                            },
                        )}
                    </Typography>
                </div>

                <div className={style.imageWrapper}>
                    {travelerUgc.pictures.slice(0, 3).map((picture) => (
                        <Ratio key={picture.id} ratio={imageRatio}>
                            <Image
                                src={picture.id}
                                alt={picture.legend}
                                fill
                                sizes="(min-width: 1200px) 600px, (min-width: 992px) 500px, 300px"
                                loader={imageLoaderForPicture(picture, {
                                    aspectRatio: imageRatio,
                                })}
                                loading="lazy"
                            />
                        </Ratio>
                    ))}
                </div>
            </div>
        </Modal>
    );
}
